/**
 * Auto-fills a new sales order's shipping and billing contacts.
 *
 * A render-less effect component: once a new order has a customer and site
 * group selected (with resolved names), it looks up each account's best
 * shipping and billing contact and seeds the form — but only when the found
 * contact carries a complete address, so a partial match leaves the form
 * blank rather than half-filled.
 *
 * Contains:
 * - ContactAutoPopulator — effect-only shipping/billing contact auto-fill.
 */
import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';

import { findAutoFillContact } from '@sophocles/components/contacts/hooks/useAutoFillContact';

const ContactAutoPopulator = ({
  setSelectedShippingContact,
  setSelectedBillingContact,
  isNewOrder,
  liveCustomerId,
  liveSiteGroupId,
  customerName,
  siteGroupName,
}) => {
  const { values, setFieldValue } = useFormikContext();

  // Use ref to track which key we've auto-filled for (avoids re-render loops)
  const autoFilledKeyRef = useRef(null);

  // Auto-fill contacts when customer/siteGroup changes (for new orders only)
  // Wait for valid names (not the default '-' value) before running
  useEffect(() => {
    const currentKey = `${liveCustomerId}-${liveSiteGroupId}`;

    if (!isNewOrder || values.id != null) return;
    if (!liveCustomerId || !liveSiteGroupId) return;
    // Wait until we have valid names (not the default '-' initialization value)
    if (!customerName || customerName === '-' || !siteGroupName || siteGroupName === '-') return;
    if (autoFilledKeyRef.current === currentKey) return; // Already auto-filled for this combo

    // Mark as auto-filled immediately to prevent duplicate calls
    autoFilledKeyRef.current = currentKey;

    const doAutoFill = async () => {
      try {
        // Auto-fill shipping contact+address combined - Sales context
        const shippingContact = await findAutoFillContact({
          customerId: liveCustomerId,
          siteGroupId: liveSiteGroupId,
          type: 'shipping',
          context: 'sales',
          customerName: customerName || 'Customer',
          siteGroupName: siteGroupName || 'Site Group',
        });

        if (shippingContact) {
          // Only auto-fill if the contact has a valid address; skip entirely
          // when address is missing so the user sees a fully empty form.
          const hasValidAddress = !!(
            shippingContact.address &&
            shippingContact.city &&
            shippingContact.state &&
            shippingContact.zipcode
          );
          if (hasValidAddress) {
            // Set combined shipping contact (name, email, phone + address fields)
            const shipData = {
              _isSynthetic: shippingContact._isAddress || shippingContact._isSynthetic || false,
              id: shippingContact.id || null,
              name: shippingContact.name || '',
              business_name: shippingContact.business_name || '',
              email: shippingContact.email || '',
              phone1: shippingContact.phone1 || '',
              address: shippingContact.address || '',
              address_2: shippingContact.address_2 || '',
              city: shippingContact.city || '',
              state: shippingContact.state || '',
              zipcode: shippingContact.zipcode || '',
              country: shippingContact.country || '',
            };
            setSelectedShippingContact(shipData);
            setFieldValue('ship_contact', shippingContact.name || '');
            setFieldValue('ship_company_name', shippingContact.business_name || '');
            setFieldValue('ship_email', shippingContact.email || '');
            setFieldValue('ship_phone', shippingContact.phone1 || '');
            setFieldValue('ship_address', shippingContact.address || '');
            setFieldValue('ship_city', shippingContact.city || '');
            setFieldValue('ship_state', shippingContact.state || '');
            setFieldValue('ship_zip', shippingContact.zipcode || '');
            setFieldValue('ship_country', shippingContact.country || '');
            const isAddress = shippingContact._isAddress || shippingContact._isSynthetic || false;
            setFieldValue('shipping_contact', isAddress ? null : shippingContact.id);
          }
        }

        // Auto-fill billing contact+address combined - Sales context
        const billingContact = await findAutoFillContact({
          customerId: liveCustomerId,
          siteGroupId: liveSiteGroupId,
          type: 'billing',
          context: 'sales',
          customerName: customerName || 'Customer',
          siteGroupName: siteGroupName || 'Site Group',
        });

        if (billingContact) {
          // Only auto-fill if the contact has a valid address; skip entirely
          // when address is missing so the user sees a fully empty form.
          const hasValidAddress = !!(
            billingContact.address &&
            billingContact.city &&
            billingContact.state &&
            billingContact.zipcode
          );
          if (hasValidAddress) {
            // Set combined billing contact (name, email, phone + address fields)
            const billData = {
              _isSynthetic: billingContact._isAddress || billingContact._isSynthetic || false,
              id: billingContact.id || null,
              name: billingContact.name || '',
              business_name: billingContact.business_name || '',
              email: billingContact.email || '',
              phone1: billingContact.phone1 || '',
              address: billingContact.address || '',
              address_2: billingContact.address_2 || '',
              city: billingContact.city || '',
              state: billingContact.state || '',
              zipcode: billingContact.zipcode || '',
              country: billingContact.country || '',
            };
            setSelectedBillingContact(billData);
            setFieldValue('bill_contact', billingContact.name || '');
            setFieldValue('bill_company_name', billingContact.business_name || '');
            setFieldValue('bill_email', billingContact.email || '');
            setFieldValue('bill_phone', billingContact.phone1 || '');
            setFieldValue('bill_address', billingContact.address || '');
            setFieldValue('bill_city', billingContact.city || '');
            setFieldValue('bill_state', billingContact.state || '');
            setFieldValue('bill_zip', billingContact.zipcode || '');
            setFieldValue('bill_country', billingContact.country || '');
            const isAddress = billingContact._isAddress || billingContact._isSynthetic || false;
            setFieldValue('billing_contact', isAddress ? null : billingContact.id);
          }
        }
      } catch (err) {
        console.error('ContactAutoPopulator: Auto-fill failed', err);
      }
    };

    doAutoFill();
  }, [isNewOrder, liveCustomerId, liveSiteGroupId, customerName, siteGroupName]);

  return null; // This component doesn't render anything
};

export default ContactAutoPopulator;
